import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface ApprovalRequest {
  id: string;
  userId: string;
  tenantId: string;
  operationType: string;
  operationData: Record<string, any>;
  amount: number;
  exceededLimits: string[];
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  requestedAt: string;
  expiresAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

export function ManagerApprovals() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [selectedApproval, setSelectedApproval] = useState<ApprovalRequest | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  const { data: pendingApprovals, isLoading: pendingLoading, refetch: refetchPending } = useQuery({
    queryKey: ['manager-pending-approvals'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.approvals.pending);
      return response.data as ApprovalRequest[];
    },
    enabled: statusFilter === 'PENDING',
  });

  const { data: historyApprovals, isLoading: historyLoading, refetch: refetchHistory } = useQuery({
    queryKey: ['manager-approvals-history', statusFilter],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.approvals.history, {
        params: { status: statusFilter !== 'ALL' ? statusFilter : undefined },
      });
      return response.data as ApprovalRequest[];
    },
    enabled: statusFilter !== 'PENDING',
  });

  const approveMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await api.post(ENDPOINTS.approvals.approve(id), { notes });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manager-pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['manager-approvals-history'] });
      setIsReviewModalOpen(false);
      setSelectedApproval(null);
      setReviewNotes('');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const response = await api.post(ENDPOINTS.approvals.reject(id), { notes });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manager-pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['manager-approvals-history'] });
      setIsReviewModalOpen(false);
      setSelectedApproval(null);
      setReviewNotes('');
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const hoursUntilExpiry = (expires.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilExpiry < 4 && hoursUntilExpiry > 0;
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const handleReview = (approval: ApprovalRequest) => {
    setSelectedApproval(approval);
    setReviewNotes('');
    setIsReviewModalOpen(true);
  };

  const handleApprove = () => {
    if (selectedApproval) {
      approveMutation.mutate({ id: selectedApproval.id, notes: reviewNotes || undefined });
    }
  };

  const handleReject = () => {
    if (selectedApproval && reviewNotes.trim()) {
      rejectMutation.mutate({ id: selectedApproval.id, notes: reviewNotes });
    }
  };

  const isLoading = statusFilter === 'PENDING' ? pendingLoading : historyLoading;
  const approvals = statusFilter === 'PENDING' ? pendingApprovals : historyApprovals;
  const refetch = statusFilter === 'PENDING' ? refetchPending : refetchHistory;

  const filteredApprovals = (approvals || []).filter(approval =>
    approval.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    approval.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    approval.operationType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusConfig: Record<string, { variant: 'success' | 'error' | 'warning' | 'info'; label: string }> = {
    PENDING: { variant: 'warning', label: 'Pendente' },
    APPROVED: { variant: 'success', label: 'Aprovado' },
    REJECTED: { variant: 'error', label: 'Rejeitado' },
    EXPIRED: { variant: 'info', label: 'Expirado' },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Aprovações</h1>
          <p className="text-surface-400">Gerencie solicitações de aprovação dos usuários</p>
        </div>
        <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome, email ou operação..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'PENDING', label: 'Pendentes' },
                { value: 'APPROVED', label: 'Aprovados' },
                { value: 'REJECTED', label: 'Rejeitados' },
                { value: 'EXPIRED', label: 'Expirados' },
                { value: 'ALL', label: 'Todos' },
              ]}
            />
          </div>
        </CardBody>
      </Card>

      {/* Approvals List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary-400" />
            Solicitações
            <Badge variant="info">{filteredApprovals.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-24" />
              ))}
            </div>
          ) : filteredApprovals.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma solicitação encontrada</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredApprovals.map(approval => (
                <div
                  key={approval.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      approval.status === 'PENDING' && isExpiringSoon(approval.expiresAt)
                        ? 'bg-red-500/20'
                        : approval.status === 'APPROVED'
                        ? 'bg-green-500/20'
                        : approval.status === 'REJECTED'
                        ? 'bg-red-500/20'
                        : 'bg-yellow-500/20'
                    }`}>
                      {approval.status === 'APPROVED' ? (
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      ) : approval.status === 'REJECTED' ? (
                        <XCircle className="w-6 h-6 text-red-400" />
                      ) : (
                        <Clock className={`w-6 h-6 ${
                          isExpiringSoon(approval.expiresAt) ? 'text-red-400' : 'text-yellow-400'
                        }`} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">{approval.user.name}</p>
                      <p className="text-sm text-surface-500">{approval.user.email}</p>
                      <p className="text-sm text-surface-400">
                        {approval.operationType} • {formatCurrency(approval.amount)}
                      </p>
                      <p className="text-xs text-surface-500">
                        Solicitado em {formatDate(approval.requestedAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <Badge variant={statusConfig[approval.status].variant}>
                        {statusConfig[approval.status].label}
                      </Badge>
                      {approval.status === 'PENDING' && isExpiringSoon(approval.expiresAt) && (
                        <p className="text-xs text-red-400 mt-1">Expira em breve</p>
                      )}
                      {approval.status === 'PENDING' && isExpired(approval.expiresAt) && (
                        <p className="text-xs text-red-400 mt-1">Expirado</p>
                      )}
                      {approval.reviewedAt && (
                        <p className="text-xs text-surface-500 mt-1">
                          Revisado em {formatDate(approval.reviewedAt)}
                        </p>
                      )}
                    </div>
                    {approval.status === 'PENDING' && !isExpired(approval.expiresAt) && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleReview(approval)}
                      >
                        Revisar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Review Modal */}
      <Modal
        isOpen={isReviewModalOpen}
        onClose={() => { setIsReviewModalOpen(false); setSelectedApproval(null); setReviewNotes(''); }}
        title="Revisar Solicitação"
        size="lg"
      >
        {selectedApproval && (
          <div className="space-y-6">
            {/* Request Details */}
            <div className="p-4 bg-surface-800/50 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="text-surface-400">Usuário</span>
                <span className="text-surface-100">{selectedApproval.user.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Email</span>
                <span className="text-surface-100">{selectedApproval.user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Operação</span>
                <span className="text-surface-100">{selectedApproval.operationType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Valor</span>
                <span className="text-surface-100 font-medium">{formatCurrency(selectedApproval.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Limites Excedidos</span>
                <div className="flex gap-1">
                  {selectedApproval.exceededLimits.map((limit, i) => (
                    <Badge key={i} variant="warning">{limit}</Badge>
                  ))}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Solicitado em</span>
                <span className="text-surface-100">{formatDate(selectedApproval.requestedAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Expira em</span>
                <span className={isExpiringSoon(selectedApproval.expiresAt) ? 'text-red-400' : 'text-surface-100'}>
                  {formatDate(selectedApproval.expiresAt)}
                </span>
              </div>
            </div>

            {/* Operation Data */}
            {selectedApproval.operationData && Object.keys(selectedApproval.operationData).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-surface-300 mb-2">Dados da Operação</h4>
                <pre className="p-3 bg-surface-900 rounded-lg text-xs text-surface-400 overflow-auto max-h-40">
                  {JSON.stringify(selectedApproval.operationData, null, 2)}
                </pre>
              </div>
            )}

            {/* Review Notes */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Observações {rejectMutation.isPending && '(obrigatório para rejeição)'}
              </label>
              <textarea
                className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500 resize-none"
                rows={3}
                placeholder="Adicione observações sobre sua decisão..."
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => { setIsReviewModalOpen(false); setSelectedApproval(null); }}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleReject}
                isLoading={rejectMutation.isPending}
                disabled={!reviewNotes.trim()}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Rejeitar
              </Button>
              <Button
                variant="primary"
                onClick={handleApprove}
                isLoading={approveMutation.isPending}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Aprovar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
}
